import { validators } from '@approvalmax/ui';
import { domain } from 'modules/data';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { isNetSuiteUrlValid } from '../../helpers';

export const stringSchema = z.string({ message: '' });
export const numberSchema = z.number({ message: '' });

/**
 * Creates a base Zod schema depending on whether the field is mandatory
 */
const createBaseSchema = (isMandatory?: boolean) => ({
    valueBoolean: z.boolean().nullish(),
    valueDecimal: isMandatory ? numberSchema.min(0, { message: '' }) : numberSchema.nullish(),
    valueLong: isMandatory ? numberSchema.min(0, { message: '' }) : numberSchema.nullish(),
    valueDate: isMandatory ? stringSchema : stringSchema.nullish(),
    valueString: isMandatory ? stringSchema : stringSchema.nullish(),
    valueRefId: isMandatory ? stringSchema : stringSchema.nullish(),
    valueRefsIds: isMandatory
        ? z.array(stringSchema, { message: '' }).nonempty({ message: '' })
        : z.array(stringSchema, { message: '' }).nullish(),
});

/**
 * Creates a Zod schema for a string field depending on its type
 */
const createStringSchema = (fieldType: string | undefined, isMandatory?: boolean) => {
    let schema = isMandatory ? stringSchema : stringSchema.nullish();

    switch (fieldType) {
        case 'EMailAddress':
            return schema.refine((val) => !val || validators.isEmail(val));

        case 'PhoneNumber':
            return schema.refine((val) => !val || isPossiblePhoneNumber(val, 'US'));

        case 'Hyperlink':
            return schema.refine((val) => !val || isNetSuiteUrlValid(val));

        default:
            return schema;
    }
};

/**
 * Creates a validation schema for a NetSuite custom field
 */
export const createCustomFieldSchema = (field: domain.Field, workflowId?: string) => {
    const isMandatoryField = field.workflows?.some(
        (workflow) => workflow.workflowId === workflowId && workflow.isMandatory
    );

    const fieldType = field.netSuiteField?.type;

    const baseSchema = createBaseSchema(isMandatoryField);

    return z.object({
        ...baseSchema,
        valueString: createStringSchema(fieldType, isMandatoryField),
    });
};
